// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionStringDataFetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * Connection String Data Fetcher.
 */

import fetch from 'share/fetch/Izenda.Fetch';
import { pick, map, assign, isEmpty } from 'lodash/fp';
export default {

  /**
   * Upload data file url.
   */
  get UPLOAD_URL() {
    return 'connection/upload';
  },

  /**
   * Upload files
   * @param  {Array} files
   * @return {Promise}
   */
  uploadFiles(files: any) {
    return fetch('connection/upload').upload(files);
  },

  /*
   * Load list of connection string
   * @returns {promise}
   */
  getAllConnectionString(tenantId: string): any {

    tenantId = isEmpty(tenantId) ? '' : tenantId;
    return fetch(`connection/${tenantId}`).get();
  },

  /**
   * Visible a connection
   * @param {string} connectionId
   * @returns {promise}
   */
  visible(connectionId: string): any {
    return fetch(`connection/visible/${connectionId}`).post();
  },

  /**
   * Invisible a connection
   * @param {string} connectionId
   * @returns {promise}
   */
  invisible(connectionId: string): any {
    return fetch(`connection/invisible/${connectionId}`).post();
  },

  /**
   * Delete a connection by its id
   * @param {string} connectionId
   * @returns {promise}
   */
  delete(connectionId: string): any {
    return fetch(`connection/${connectionId}`).delete();
  },

  /**
   * Delete a connection schema  from solar by its id
   * @param {string} connectionId
   * @returns {promise}
   */
  deleteSchemaFromSolar(connectionId: string): any {
    return fetch(`connection/DeleteSolarConnection/${connectionId}`).delete();
  },
  /**
   * Delete temp excel files
   * @param {type} data
   * @returns {promise}
   */
  deleteTempExcelFiles(data: any): any {
    return fetch(`connection/deleteTempExcelFiles`).post(data);
  },

  /**
   * Test a connection string (well-format and can work)
   * @param {type} data
   * @returns {promise}
   */
  testConnectionString(data: any): any {
    return fetch('connection/verify').post(data);
  },

  /**
   * Load supporting database server types
   * @returns {promise}
   */
  getSupportedDatabaseType(): any {
    return fetch('dataadaptor/').get();
  },

  /**
   * Load database name for a connection
   * @param {Object} databaseNameRequest
   * @returns {promise}
   */
  getDatabaseName(databaseNameRequest: any): any {
    return fetch('connection/DatabaseName').post(databaseNameRequest);
  },

  /**
   * Load schema of a database
   * @param {string} connectionString
   * @param {string} serverType
   * @returns {promise}
   */
  getRemoteSchema(connectionString: string, serverType: string): any {
    const params = {
      connectionString,
      serverTypeId: serverType
    };

    return fetch('connection/loadRemoteSchema').post(params);
  },

  /**
   * Load detail of connection
   * @param {String} connectionId
   * @returns {promise}
   */
  getConnectionString(connectionId: string): any {
    return fetch(`connection/detail/${connectionId}`).get();
  },

  /**
   * Load connection string preset data. Can be used as template for connection string builder.
   * @param {string} serverTypeId
   * @returns {Promise}
   */
  getConnectionStringPreset(serverTypeId) {
    return fetch(`connection/preset/${serverTypeId}`).get();
  },

  /**
   * Load data to restore connection from connection string.
   * @param {object} data
   * @returns {Promise}
   */
  getConnectionStringData(data) {
    return fetch(`connection/connectionStringData`).post(data);
  },

  /**
   * Load Source connections
   * @param {String} sourceId
   * @returns {promise}
   */
  getConnectionDetailInfo(sourceId: string, isLoadField: boolean, params: any): any {
    const paramsText = $izenda.param(params || {});

    return fetch(`connection/detailInfo/${sourceId}/${isLoadField}?${paramsText}`).get();
  },

  /**
   * Load connections  by query sources
   * @param {Object} request
   * @returns {promise}
   */
  getConnectionDetailInfoByQuerySoures(request: any): any {
    return fetch('connection/detailInfoByQuerySources').post(request);
  },

  /**
   * Load Source connections (lazy)
   * @param {Object} request
   * @returns {promise}
   */
  getLazyConnectionDetailInfo(request: any): any {
    return fetch('connection/detailInfo').post(request);
  },

  /**
   * Update connection details
   * @param {Object} connectionStringDetail
   * @returns {promise}
   */
  updateConnectionString(connectionStringDetail: any): any {
    const postData = connectionStringDetail;

    const pickQuerySourceCategory = pick(['id', 'connectionId', 'name']);
    const pickQuerySource = pick(['id', 'name', 'type', 'selected', 'physicalChange', 'approval', 'categoryId']);

    const querySources = map(category => {
      const props = pickQuerySourceCategory(category);
      const querySources = map(pickQuerySource, category.querySources);
      return assign(props, {
        querySources
      });
    }, postData.dBSource.querySources);

    postData.dBSource.querySources = querySources;

    return fetch('connection').post(postData);
  },

  /**
   * Validate schema
   * @param {Object} connectionStringDetail
   * @returns {promise}
   */
  validateSchema(connectionStringDetail: any): any {
    return fetch('connection/validateSchema').post(connectionStringDetail);
  },

  /**
   * reload remote schema
   * @param {Object} connectionDetail
   * @returns {promise}
   */
  reloadRemoteSchema(connectionDetail: any): any {
    const params = {
      connectionId: connectionDetail.id,
      connectionString: connectionDetail.connectionString,
      serverTypeId: connectionDetail.serverTypeId
    };

    return fetch('connection/reloadRemoteSchema').post(params);
  },

    /**
   * update reload remote schema to solar
   * @param {Object} connectionDetail
   * @returns {promise}
   */
  updateSchemaToSolar(connectionDetail: any): any {
    const params = {
      connectionId: connectionDetail.id,
      connectionString: connectionDetail.connectionString,
      serverTypeId: connectionDetail.serverTypeId
    };

    return fetch('connection/updateSolarSchema').post(params);
  }
};



// WEBPACK FOOTER //
// ./ui/fetchers/Izenda.ConnectionStringDataFetcher.js